import React, { useEffect, useState } from 'react';
import {
  CategoriaLancamentoQualidade,
  IAuditoriaQualidadeHistoricosResultado,
  IItemQualidadeConta,
  ILancamentoDesqualificado,
  ILancamentosQualidadePagina,
} from '../interfaces/IAuditoriaQualidadeHistoricos';
import { AuditoriaQualidadeHistoricosService } from '../services/AuditoriaQualidadeHistoricosService';
import { exportarXLSX, exportarCSV, nomeArquivoSeguro } from '../utils/exportarAuditoria';

interface Props {
  resultado: IAuditoriaQualidadeHistoricosResultado;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (value?: string) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('pt-BR');
};

// COR-324: uma linha por lançamento (desqualificado OU pendente), achatando todas as contas — o
// mesmo formato serve para XLSX e CSV. Reaproveita o utilitário compartilhado
// utils/exportarAuditoria (pensado para todos os épicos do Revisor Contábil, não só este).
interface LinhaExportacaoQualidade {
  [coluna: string]: string;
  Empresa: string;
  'Conta Contábil': string;
  'Descrição da Conta': string;
  Referencial: string;
  Data: string;
  'Nº do Lançamento': string;
  'Histórico': string;
  'Valor (R$)': string;
  'Classificação': string;
  'Motivo da Classificação': string;
  Status: string;
  'Risco Fiscal Estimado (R$)': string;
  'Situação da Validação Documental': string;
}

const montarLinhaExportacao = (
  conta: IItemQualidadeConta,
  lanc: ILancamentoDesqualificado,
  classificacao: 'ERRO' | 'PENDENTE'
): LinhaExportacaoQualidade => ({
  Empresa: conta.empresaNome,
  'Conta Contábil': lanc.contaContabil,
  'Descrição da Conta': conta.descricaoConta,
  Referencial: conta.contaReferencial,
  Data: formatDate(lanc.dataLancamento),
  'Nº do Lançamento': lanc.numeroLancamento || String(lanc.lancamentoId),
  'Histórico': lanc.historico,
  'Valor (R$)': lanc.valor.toFixed(2).replace('.', ','),
  'Classificação': classificacao === 'ERRO' ? 'ERRO — Histórico Desqualificado' : 'PENDENTE — Evidência Parcial',
  'Motivo da Classificação': lanc.mensagemDiagnostico,
  Status: classificacao,
  'Risco Fiscal Estimado (R$)': lanc.multa.toFixed(2).replace('.', ','),
  'Situação da Validação Documental':
    classificacao === 'ERRO' ? 'Desqualificado — sem NF/Razão Social' : 'Pendente de validação manual',
});

const montarLinhasExportacaoQualidade = (contas: IItemQualidadeConta[]): LinhaExportacaoQualidade[] => {
  const linhas: LinhaExportacaoQualidade[] = [];
  contas.forEach(conta => {
    conta.lancamentosDesqualificados.forEach(lanc => linhas.push(montarLinhaExportacao(conta, lanc, 'ERRO')));
    conta.lancamentosPendentes.forEach(lanc => linhas.push(montarLinhaExportacao(conta, lanc, 'PENDENTE')));
  });
  return linhas;
};

const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO': return 'bi-exclamation-circle-fill';
    case 'ALERTA': return 'bi-exclamation-triangle-fill';
    case 'AVISO': return 'bi-info-circle-fill';
    case 'REFERENCIAL_DIVERGENTE': return 'bi-signpost-split-fill';
    case 'PENDENTE': return 'bi-question-circle-fill';
    default: return 'bi-check-circle-fill';
  }
};

// COR-219: rótulo legível — evita mostrar "REFERENCIAL_DIVERGENTE" cru (nome interno de status) na tela.
const getRotuloStatus = (status: string) => (status === 'REFERENCIAL_DIVERGENTE' ? 'Referencial Divergente' : status);

const BarraQualidade: React.FC<{ percentual: number }> = ({ percentual }) => {
  const cor = percentual >= 90 ? '#15803d' : percentual >= 70 ? '#b45309' : '#9b1c1c';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{ flex: 1, height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(percentual, 100)}%`, height: '100%', background: cor, borderRadius: '4px' }} />
      </div>
      <span style={{ fontSize: '12px', fontWeight: 600, color: cor, minWidth: '45px' }}>
        {percentual.toFixed(1)}%
      </span>
    </div>
  );
};

// DEVN3-593: tabela de lançamentos (desqualificados ou pendentes) — a MESMA marcação serve às listas
// inline (resultado calculado on-demand, empresas pequenas) e às páginas buscadas sob demanda do
// resultado pré-processado (empresas com centenas de milhares de linhas).
interface TabelaLancamentosProps {
  lancamentos: ILancamentoDesqualificado[];
  chavePrefixo: string;
  comMulta: boolean;
  corLinha: string;
  corDetalhe: string;
  lancamentoExpandido: string | null;
  alternarLancamento: (chave: string, e: React.MouseEvent) => void;
}

const TabelaLancamentos: React.FC<TabelaLancamentosProps> = ({
  lancamentos,
  chavePrefixo,
  comMulta,
  corLinha,
  corDetalhe,
  lancamentoExpandido,
  alternarLancamento,
}) => {
  const colunas = comMulta ? 6 : 5;

  return (
    <div className='table-responsive mt-3'>
      <table className='auditoria-table' style={{ fontSize: '12px' }}>
        <thead>
          <tr>
            <th>Data</th>
            <th>Conta Contábil</th>
            <th>Histórico</th>
            <th className='text-end'>Valor</th>
            {comMulta && <th className='text-end'>Multa ECD Potencial (Lançamento)</th>}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {lancamentos.map((lanc: ILancamentoDesqualificado, li) => {
            const chaveLanc = `${chavePrefixo}-${li}`;
            const lancAberto = lancamentoExpandido === chaveLanc;
            return (
              <React.Fragment key={chaveLanc}>
                <tr
                  style={{ cursor: 'pointer', background: corLinha }}
                  onClick={(e) => alternarLancamento(chaveLanc, e)}
                >
                  <td>{formatDate(lanc.dataLancamento)}</td>
                  <td>{lanc.contaContabil}</td>
                  <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {lanc.historico || <span className='text-muted'>(vazio)</span>}
                  </td>
                  <td className='text-end'>{formatCurrency(lanc.valor)}</td>
                  {comMulta && (
                    <td className='text-end' style={{ color: '#9b1c1c' }}>
                      {formatCurrency(lanc.multa)}
                    </td>
                  )}
                  <td>
                    <i className={`bi ${lancAberto ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted`} />
                  </td>
                </tr>
                {lancAberto && (
                  <tr>
                    <td colSpan={colunas} style={{ background: corDetalhe, padding: '8px 12px', fontSize: '11px' }}>
                      {lanc.mensagemDiagnostico}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const TAMANHO_PAGINA_LANCAMENTOS = 50;

interface LancamentosPaginadosProps {
  esteiraId: number;
  referencial: string;
  categoria: CategoriaLancamentoQualidade;
  total: number;
  chavePrefixo: string;
  comMulta: boolean;
  corLinha: string;
  corDetalhe: string;
  lancamentoExpandido: string | null;
  alternarLancamento: (chave: string, e: React.MouseEvent) => void;
}

// DEVN3-593: resultado pré-processado não traz as linhas dentro do JSON (podem ser centenas de
// milhares) — busca 1 página por vez em GET auditoria-qualidade-historicos/lancamentos.
const LancamentosPaginados: React.FC<LancamentosPaginadosProps> = ({
  esteiraId,
  referencial,
  categoria,
  total,
  chavePrefixo,
  comMulta,
  corLinha,
  corDetalhe,
  lancamentoExpandido,
  alternarLancamento,
}) => {
  const [pagina, setPagina] = useState(1);
  const [dados, setDados] = useState<ILancamentosQualidadePagina | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    setErro(null);

    AuditoriaQualidadeHistoricosService.buscarLancamentos({
      esteiraId,
      referencial,
      categoria,
      pagina,
      tamanhoPagina: TAMANHO_PAGINA_LANCAMENTOS,
    })
      .then(resposta => {
        if (!cancelado) setDados(resposta);
      })
      .catch(() => {
        if (!cancelado) setErro('Não foi possível carregar os lançamentos.');
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [esteiraId, referencial, categoria, pagina]);

  const totalPaginas = Math.max(1, Math.ceil((dados?.total ?? total) / TAMANHO_PAGINA_LANCAMENTOS));

  if (dados?.restrito) {
    return (
      <div className='text-muted mt-3'>
        O detalhe dos lançamentos não está disponível no seu plano de acesso à Auditoria.
      </div>
    );
  }

  return (
    <div>
      {erro && <div className='text-danger mt-2'>{erro}</div>}

      {dados && dados.itens.length > 0 && (
        <TabelaLancamentos
          lancamentos={dados.itens}
          chavePrefixo={`${chavePrefixo}-p${pagina}`}
          comMulta={comMulta}
          corLinha={corLinha}
          corDetalhe={corDetalhe}
          lancamentoExpandido={lancamentoExpandido}
          alternarLancamento={alternarLancamento}
        />
      )}

      {carregando && !dados && <div className='text-muted mt-2'>Carregando lançamentos…</div>}

      <div className='d-flex align-items-center gap-2 mt-2' style={{ fontSize: '12px' }}>
        <button
          type='button'
          className='btn btn-sm btn-light'
          disabled={carregando || pagina <= 1}
          onClick={(e) => { e.stopPropagation(); setPagina(pagina - 1); }}
        >
          Anterior
        </button>
        <span>
          Página {pagina} de {totalPaginas} — {(dados?.total ?? total).toLocaleString('pt-BR')} lançamento(s)
          {carregando && ' (carregando…)'}
        </span>
        <button
          type='button'
          className='btn btn-sm btn-light'
          disabled={carregando || pagina >= totalPaginas}
          onClick={(e) => { e.stopPropagation(); setPagina(pagina + 1); }}
        >
          Próxima
        </button>
      </div>
    </div>
  );
};

const AuditoriaQualidadeHistoricosDetalhe: React.FC<Props> = ({ resultado }) => {
  const [contaExpandida, setContaExpandida] = useState<string | null>(null);
  const [lancamentoExpandido, setLancamentoExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);
  const [exportandoEsteira, setExportandoEsteira] = useState<number | null>(null);
  const [erroExportacao, setErroExportacao] = useState<string | null>(null);

  const contas = resultado.contas || [];

  // COR-324: exporta todos os lançamentos (desqualificados + pendentes) das contas já carregadas
  // na tela — respeita a empresa/período/filtros já aplicados no resultado exibido.
  const linhasExportacao = montarLinhasExportacaoQualidade(contas);

  // DEVN3-593: resultado pré-processado não traz as linhas (só as contagens) — o CSV completo é gerado
  // no servidor, 1 por esteira. Contas com listas inline continuam exportando no navegador (xlsx/csv acima).
  const esteirasParaExportarNoServidor = Array.from(
    new Set(
      contas
        .filter(c => c.esteiraId
          && c.linhasComErro + c.linhasPendentes > 0
          && c.lancamentosDesqualificados.length === 0
          && c.lancamentosPendentes.length === 0)
        .map(c => c.esteiraId as number)
    )
  );

  const exportarCsvDoServidor = async (esteiraId: number) => {
    try {
      setErroExportacao(null);
      setExportandoEsteira(esteiraId);
      await AuditoriaQualidadeHistoricosService.exportarCsv({ esteiraId });
    } catch {
      setErroExportacao('Não foi possível gerar o CSV. Tente novamente.');
    } finally {
      setExportandoEsteira(null);
    }
  };
  const nomeBaseExportacao = nomeArquivoSeguro(
    `qualidade_historicos_${contas[0]?.empresaNome || 'empresa'}`
  );

  const alternarConta = (chave: string) => {
    setContaExpandida(contaExpandida === chave ? null : chave);
    setLancamentoExpandido(null);
  };

  const alternarLancamento = (chave: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setLancamentoExpandido(lancamentoExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>
            Auditoria de Qualidade dos Históricos — Fornecedores e Clientes — {getRotuloStatus(resultado.status)}
          </strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Risco Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>
              Multa ECD potencial (5% limitada a 1% da receita bruta do período) para lançamentos de
              constituição de obrigação/receita sem NF e sem Razão Social — sujeita a confirmação, não
              é penalidade já aplicada (art. 12, II, Lei nº 8.218/1991).
            </div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Contas Analisadas</div>
          <div className='auditoria-kpi-value'>{contas.length}</div>
          <div className='auditoria-kpi-help'>
            Contas com referencial 2.01.01.03.01 (Fornecedores) e 1.01.02.02.01 (Clientes) identificadas na ECD.
          </div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>
          </div>
          <div className='auditoria-kpi-help'>
            ERRO: índice abaixo de 90% (Risco de Auditoria). ALERTA: erros encontrados mas acima do limiar.
          </div>
        </div>

        <div className={`auditoria-kpi ${resultado.totalPendentes > 0 ? 'auditoria-kpi-alerta' : ''}`}>
          <div className='auditoria-kpi-label'>Pendentes de Validação</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-pendente'>{resultado.totalPendentes}</span>
          </div>
          <div className='auditoria-kpi-help'>
            Soma das linhas (de todas as contas) com evidência parcial — nome identificável, ajuste/reclassificação,
            ou liquidação sem vínculo confirmado — sem NF/Razão Social; requer validação manual, sem multa automática.
          </div>
        </div>

        {/* COR-217: composição real da base analisada, em nível de LINHA — separa claramente erro,
            pendente, qualificada e "sem multa aplicada" (qualificada + pendente). */}
        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Composição das Linhas Analisadas</div>
          <div className='auditoria-kpi-value' style={{ fontSize: 13, fontWeight: 600 }}>
            {resultado.totalLinhasAnalisadas} linha{resultado.totalLinhasAnalisadas === 1 ? '' : 's'}
          </div>
          <div className='auditoria-kpi-help'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalLinhasComErro}</span> com erro (multa) ·{' '}
            <span className='auditoria-contador auditoria-contador-pendente'>{resultado.totalPendentes}</span> pendentes ·{' '}
            <span className='auditoria-contador auditoria-contador-secundario'>{resultado.totalLinhasQualificadas}</span> qualificadas ·{' '}
            {resultado.totalLinhasSemMulta} sem multa aplicada
          </div>
        </div>

        {resultado.totalReferencialDivergente > 0 && (
          <div className='auditoria-kpi auditoria-kpi-alerta'>
            <div className='auditoria-kpi-label'>Indício de Divergência Cadastral</div>
            <div className='auditoria-kpi-value'>
              <span className='auditoria-contador auditoria-contador-secundario'>{resultado.totalReferencialDivergente}</span>
            </div>
            <div className='auditoria-kpi-help'>
              Conta com descrição de instituição financeira mapeada como Fornecedores/Clientes na ECD — indício
              complementar para revisão cadastral; a análise de qualidade continua rodando normalmente para essa conta.
            </div>
          </div>
        )}

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>NBC TG 1000, ITG 2000, IN RFB 2003/2021, Lei 8.218/1991 Art. 12.</div>
        </div>
      </div>

      {esteirasParaExportarNoServidor.length > 0 && (
        <div className='d-flex flex-wrap gap-2 align-items-center mt-3 mb-1'>
          <strong style={{ marginRight: 4, fontSize: '0.85rem' }}>
            <i className='bi bi-download me-1' />
            Exportar todos os lançamentos (CSV):
          </strong>
          {esteirasParaExportarNoServidor.map(esteiraId => (
            <button
              key={esteiraId}
              type='button'
              className='btn btn-sm btn-outline-primary'
              disabled={exportandoEsteira === esteiraId}
              onClick={() => exportarCsvDoServidor(esteiraId)}
            >
              {exportandoEsteira === esteiraId
                ? 'Gerando CSV…'
                : esteirasParaExportarNoServidor.length > 1
                  ? `CSV — esteira ${esteiraId}`
                  : 'CSV completo'}
            </button>
          ))}
          {erroExportacao && <span className='text-danger' style={{ fontSize: '0.8rem' }}>{erroExportacao}</span>}
        </div>
      )}

      {linhasExportacao.length > 0 && (
        <div className='d-flex flex-wrap gap-2 align-items-center mt-3 mb-1'>
          <strong style={{ marginRight: 4, fontSize: '0.85rem' }}>
            <i className='bi bi-download me-1' />
            Exportar lançamentos ({linhasExportacao.length}):
          </strong>
          <button
            type='button'
            className='btn btn-sm btn-outline-primary'
            onClick={() => exportarXLSX(linhasExportacao, nomeBaseExportacao, 'Qualidade Históricos')}
          >
            Excel (.xlsx)
          </button>
          <button
            type='button'
            className='btn btn-sm btn-outline-primary'
            onClick={() => exportarCSV(linhasExportacao, nomeBaseExportacao)}
          >
            CSV
          </button>
        </div>
      )}

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Base Legal — Qualidade dos Históricos:</strong> {resultado.fundamentacaoLegal}</div>
        </div>
      )}

      <div className='auditoria-card'>
        <h3>Análise por Conta — Qualidade dos Históricos</h3>
        <p className='text-muted'>
          O índice de qualidade corresponde à proporção de lançamentos com histórico qualificado
          (com Nota Fiscal <strong>ou</strong> Razão Social identificados). Índice abaixo de 90% classifica
          a empresa como <strong>Inelegível para Conciliação Automática</strong>. Clique na conta para ver
          os lançamentos desqualificados.
        </p>

        {contas.length === 0 && (
          <div className='text-muted mt-4'>
            Nenhuma conta de Fornecedores (2.01.01.03.01) ou Clientes (1.01.02.02.01) identificada na ECD do período.
          </div>
        )}

        {contas.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Empresa / Conta</th>
                  <th>Índice de Qualidade</th>
                  <th className='text-center'>Total Linhas</th>
                  <th className='text-center'>Linhas c/ Erro</th>
                  <th className='text-center' title='Evidência parcial (nome identificável, ajuste/reclassificação ou liquidação sem vínculo confirmado) — sem multa automática'>Pendentes</th>
                  <th className='text-end'>Multa ECD Potencial</th>
                  <th>Elegibilidade</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {contas.map((conta: IItemQualidadeConta, idx) => {
                  const chave = `qh-${conta.empresaId}-${conta.contaReferencial}-${idx}`;
                  const aberta = contaExpandida === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarConta(chave)}>
                        <td>
                          <div><strong>{conta.empresaNome}</strong></div>
                          <div className='text-muted' style={{ fontSize: '12px' }}>
                            {conta.contaReferencial} — {conta.descricaoConta}
                          </div>
                          <div className='text-muted' style={{ fontSize: '11px' }}>CNPJ: {conta.cnpj}</div>
                        </td>
                        <td style={{ minWidth: '160px' }}>
                          <BarraQualidade percentual={conta.percentualQualidade} />
                          {conta.referencialDivergente && (
                            <div className='text-muted' style={{ fontSize: '10px', marginTop: 2 }}>
                              <i className='bi bi-signpost-split-fill me-1' />indício de divergência cadastral
                            </div>
                          )}
                        </td>
                        <td className='text-center'>{conta.totalLinhas}</td>
                        <td className='text-center'>
                          {conta.linhasComErro > 0
                            ? <strong style={{ color: '#9b1c1c' }}>{conta.linhasComErro}</strong>
                            : <span className='text-muted'>0</span>}
                        </td>
                        <td className='text-center'>
                          {conta.linhasPendentes > 0
                            ? <span className='auditoria-contador auditoria-contador-pendente'>{conta.linhasPendentes}</span>
                            : <span className='text-muted'>0</span>}
                        </td>
                        <td className='text-end'>
                          {conta.multaEcdEstimada > 0
                            ? <strong style={{ color: '#9b1c1c' }}>{formatCurrency(conta.multaEcdEstimada)}</strong>
                            : <span className='text-muted'>—</span>}
                        </td>
                        <td>
                          <span
                            className={`auditoria-status ${conta.elegibilidadeConciliacao === 'INELEGIVEL' ? 'status-ERRO' : 'status-CONCILIADO'}`}
                            style={{ fontSize: '11px' }}
                          >
                            {conta.elegibilidadeConciliacao === 'INELEGIVEL' ? 'INELEGÍVEL' : 'ELEGÍVEL'}
                          </span>
                        </td>
                        <td>
                          <span className={`auditoria-status status-${conta.status}`}>{getRotuloStatus(conta.status)}</span>
                          <i className={`bi ${aberta ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {aberta && (
                        <tr>
                          <td colSpan={8} style={{ background: '#fafbfc', padding: 0 }}>
                            <div className='py-3 px-4'>
                              <div className='mb-2'>
                                <strong>Parecer:</strong> {conta.parecerQualificacao}
                              </div>

                              {conta.multaEcdEstimada > 0 && (
                                <div className='text-muted fs-9 mb-2'>
                                  Operações irregulares: {formatCurrency(conta.operacoesIrregularesEcd)} × 5% = {formatCurrency(conta.multaBruta5Ecd)}
                                  {' | '}Receita Bruta do Período: {conta.receitaBrutaPeriodoEcd != null ? formatCurrency(conta.receitaBrutaPeriodoEcd) : 'não disponível'}
                                  {conta.tetoLegal1Ecd != null && <> × 1% = {formatCurrency(conta.tetoLegal1Ecd)}</>}
                                  {' | '}Critério aplicado: {conta.criterioAplicadoEcd ?? conta.statusEvidenciaEcd}
                                </div>
                              )}

                              {/* DEVN3-593: linhas inline (calculo on-demand, empresa pequena) OU paginadas
                                  sob demanda (resultado pre-processado — a lista vem vazia com contagem > 0). */}
                              {conta.linhasComErro === 0 && conta.lancamentosDesqualificados.length === 0 && (
                                <div className='text-muted'>Nenhum lançamento desqualificado — todos os históricos possuem NF ou Razão Social.</div>
                              )}

                              {conta.lancamentosDesqualificados.length > 0 && (
                                <TabelaLancamentos
                                  lancamentos={conta.lancamentosDesqualificados}
                                  chavePrefixo={`${chave}-lanc`}
                                  comMulta
                                  corLinha='#fff7f7'
                                  corDetalhe='#fff1f2'
                                  lancamentoExpandido={lancamentoExpandido}
                                  alternarLancamento={alternarLancamento}
                                />
                              )}

                              {conta.lancamentosDesqualificados.length === 0 && conta.linhasComErro > 0 && conta.esteiraId ? (
                                <LancamentosPaginados
                                  esteiraId={conta.esteiraId}
                                  referencial={conta.contaReferencial}
                                  categoria='ERRO'
                                  total={conta.linhasComErro}
                                  chavePrefixo={`${chave}-lanc`}
                                  comMulta
                                  corLinha='#fff7f7'
                                  corDetalhe='#fff1f2'
                                  lancamentoExpandido={lancamentoExpandido}
                                  alternarLancamento={alternarLancamento}
                                />
                              ) : null}

                              {(conta.lancamentosPendentes.length > 0 || (conta.linhasPendentes > 0 && conta.esteiraId)) && (
                                <div className='mt-3'>
                                  <div className='mb-1' style={{ fontSize: '12px', fontWeight: 600 }}>
                                    Pendentes de validação manual (evidência parcial — sem NF/Razão Social confirmados; sem multa)
                                  </div>

                                  {conta.lancamentosPendentes.length > 0 ? (
                                    <TabelaLancamentos
                                      lancamentos={conta.lancamentosPendentes}
                                      chavePrefixo={`${chave}-pend`}
                                      comMulta={false}
                                      corLinha='#eef2ff'
                                      corDetalhe='#eef2ff'
                                      lancamentoExpandido={lancamentoExpandido}
                                      alternarLancamento={alternarLancamento}
                                    />
                                  ) : (
                                    <LancamentosPaginados
                                      esteiraId={conta.esteiraId as number}
                                      referencial={conta.contaReferencial}
                                      categoria='PENDENTE'
                                      total={conta.linhasPendentes}
                                      chavePrefixo={`${chave}-pend`}
                                      comMulta={false}
                                      corLinha='#eef2ff'
                                      corDetalhe='#eef2ff'
                                      lancamentoExpandido={lancamentoExpandido}
                                      alternarLancamento={alternarLancamento}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default AuditoriaQualidadeHistoricosDetalhe;
